import type { Survey, SurveyScoresByQuestion } from '@engagement-web-app/common';
import { uuidSchema } from '@engagement-web-app/common/validation-utils';
import {
    createFileRoute,
    type ErrorComponentProps,
    redirect,
    retainSearchParams,
    SearchParamError,
    stripSearchParams,
} from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useState } from 'react';
import { flushSync } from 'react-dom';
import { z } from 'zod';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { InvalidRequest } from '@/components/InvalidRequest';
import { NotFound, NotFoundError } from '@/components/NotFound';
import { Page } from '@/components/Page';
import { RouteError } from '@/components/RouteError';
import { Skeleton } from '@/components/Skeleton';
import { CreatePdfButton } from '@/components/surveys/common/CreatePdfButton';
import { Ranking, RankingSkeleton } from '@/components/surveys/common/Ranking';
import { SelectSurvey } from '@/components/surveys/common/SelectSurvey';
import {
    SurveyQuestionsComparisonTable,
    SurveyQuestionsComparisonTableSkeleton,
} from '@/components/surveys/questions/SurveyQuestionsComparisonTable';
import { fetchSurveys } from '@/queries/surveys/client';
import { pageBreakBefore } from '@/styles/print.css';
import { formatDateToLabel } from '@/utils';
import { useScoresByQuestion, useScoresForQuestionsComparisonTable, useSurveyAttributes } from '../-hooks';
import { resolveScoresChain } from '../-utils';

const DefaultRouteQueryParams = {
    surveyId: '',
};

const RouteQueryParams = z.object({
    surveyId: uuidSchema.default(DefaultRouteQueryParams.surveyId),
    /** A question to bring into view in the question scores table. */
    questionId: z.string().optional(),
});

export const Route = createFileRoute('/_client/_auth/surveys/questions/')({
    component: SurveyQuestionsRoute,
    validateSearch: zodValidator(RouteQueryParams),
    search: {
        middlewares: [retainSearchParams(['surveyId']), stripSearchParams(DefaultRouteQueryParams)],
    },
    loaderDeps: ({ search }) => ({ surveyId: search.surveyId }),
    loader: async ({ deps }) => {
        const { surveys } = await fetchSurveys();

        if (deps.surveyId === '' && surveys.length > 0) {
            // Canonicalizes the URL to the survey actually being shown, so a shared/reloaded
            // link keeps pointing at it rather than silently drifting to whatever's newest.
            throw redirect({ to: '/surveys/questions', search: { surveyId: surveys[0].surveyId }, replace: true });
        }

        if (!surveys.some((survey) => survey.surveyId === deps.surveyId)) {
            throw new NotFoundError();
        }

        return { surveys };
    },
    pendingComponent: SurveyQuestionsRouteSkeleton,
    errorComponent: SurveyQuestionsRouteError,
});

function SurveyQuestionsRoute() {
    // Paginated content has to be expanded before the browser captures the page.
    const [isPrinting, setIsPrinting] = useState(false);
    const { surveys } = Route.useLoaderData();
    const { surveyId, questionId } = Route.useSearch();
    const navigate = Route.useNavigate();

    // The loader redirects an empty `surveyId` to the newest survey's id, and rejects a
    // non-matching one, so this is always a valid index once there's at least one survey.
    const selectedSurveyIdx = surveys.findIndex((s) => s.surveyId === surveyId);
    const surveysToLoad = surveys.slice(selectedSurveyIdx);

    const scoresByQuestionQuery = useScoresByQuestion(surveysToLoad.map((s) => s.surveyId));

    if (surveys.length === 0) {
        // TODO: Visual design for empty state
        return <div>サーベイがありません。</div>;
    }

    const selectedSurvey = surveys[selectedSurveyIdx];

    return (
        <Page.Root>
            <Page.Header title="結果分析 - 設問" />

            <Page.Content gap="5">
                <Flex align="end" justify="space-between">
                    <SelectSurvey
                        surveys={surveys.map((survey) => ({
                            id: survey.surveyId,
                            name: formatDateToLabel(survey.surveyDate),
                        }))}
                        selectedSurveyId={selectedSurvey.surveyId}
                        onSurveyChange={(id) => navigate({ search: { surveyId: id } })}
                    />

                    <CreatePdfButton
                        beforeOpenPrint={() => flushSync(() => setIsPrinting(true))}
                        afterClosePrint={() => setIsPrinting(false)}
                    />
                </Flex>

                {scoresByQuestionQuery.isPending && <ContentSkeleton />}

                <ErrorOverlay
                    active={scoresByQuestionQuery.isError}
                    onRetry={scoresByQuestionQuery.refetch}
                    message="設問分析の取得に失敗しました。"
                />

                {scoresByQuestionQuery.data && selectedSurvey && (
                    <SurveyQuestions
                        scores={resolveScoresChain(surveysToLoad, scoresByQuestionQuery.data.scores)}
                        selectedSurvey={selectedSurvey}
                        focusedQuestionId={questionId}
                        isPrinting={isPrinting}
                        isError={scoresByQuestionQuery.isError}
                        onRetry={scoresByQuestionQuery.refetch}
                    />
                )}
            </Page.Content>
        </Page.Root>
    );
}

function SurveyQuestionsRouteSkeleton() {
    return (
        <Page.Root>
            {/* The title is static, so it renders for real rather than as a placeholder. */}
            <Page.Header title="結果分析 - 設問" />

            <Page.Content gap="5">
                <Flex align="end" justify="space-between">
                    {/* Absolute widths: these sit in shrink-to-fit flex items, so a
                        percentage would have no definite width to resolve against. */}
                    <Flex direction="column" gap="2">
                        <Skeleton.Line width={96} height="sm" />
                        <Skeleton.Box width={300} height={42} />
                    </Flex>

                    <Skeleton.Box width={168} height={36} />
                </Flex>

                <ContentSkeleton />
            </Page.Content>
        </Page.Root>
    );
}

function SurveyQuestionsRouteError({ error, reset }: ErrorComponentProps) {
    if (error instanceof SearchParamError) {
        return <InvalidRequest kind="client" />;
    }

    if (error instanceof NotFoundError) {
        return <NotFound kind="client" />;
    }

    return (
        <RouteError
            kind="client"
            title="結果分析 - 設問"
            onRetry={reset}
            message="結果分析 - 設問の取得に失敗しました。"
        />
    );
}
type SurveyQuestionsProps = {
    scores: SurveyScoresByQuestion[];
    selectedSurvey: Survey;
    focusedQuestionId?: string;
    isPrinting: boolean;
    isError: boolean;
    onRetry: () => void;
};

function SurveyQuestions({
    scores,
    selectedSurvey,
    focusedQuestionId,
    isPrinting,
    isError,
    onRetry,
}: SurveyQuestionsProps) {
    const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([]);

    const selectedSurveyId = selectedSurvey.surveyId;
    const previousSurveyId = selectedSurvey.previousSurveyId;

    const currentSurveyScores = scores.find((s) => s.surveyId === selectedSurveyId)!;

    const surveyAttributesQuery = useSurveyAttributes(selectedSurveyId);

    const {
        scoresForQuestionsComparisonTable,
        previousScoresForQuestionsComparisonTable,
        isError: isQuestionsComparisonTableError,
        retry: retryQuestionsComparisonTable,
    } = useScoresForQuestionsComparisonTable(selectedSurveyId, previousSurveyId, selectedOptionIds);

    const mappedItemsForRanking = currentSurveyScores.questions.map((q) => ({
        name: q.text,
        di: q.di,
        deviation: q.deviation,
    }));

    return (
        <Flex direction="column" gap="5">
            <Flex gap="4">
                <Ranking
                    title="よかった​設問"
                    variant="positive"
                    items={mappedItemsForRanking}
                    isError={isError}
                    onRetry={onRetry}
                />
                <Ranking
                    title="改善必要設問"
                    variant="negative"
                    items={mappedItemsForRanking}
                    isError={isError}
                    onRetry={onRetry}
                />
            </Flex>

            {/* Print layout: the rankings, then the question scores. */}
            <div className={pageBreakBefore}>
                <SurveyQuestionsComparisonTable
                    scores={scoresForQuestionsComparisonTable.data ?? null}
                    previousScores={previousScoresForQuestionsComparisonTable.data ?? null}
                    attributeSettings={surveyAttributesQuery.data?.attributes ?? null}
                    selectedAttributeOptionIds={selectedOptionIds}
                    onSelectedAttributeOptionIdsChange={setSelectedOptionIds}
                    isFetching={
                        scoresForQuestionsComparisonTable.isFetching ||
                        previousScoresForQuestionsComparisonTable.isFetching
                    }
                    isError={isQuestionsComparisonTableError}
                    onRetry={retryQuestionsComparisonTable}
                    withPagination={!isPrinting}
                    focusedQuestionId={focusedQuestionId}
                />
            </div>
        </Flex>
    );
}

function ContentSkeleton() {
    return (
        <Flex direction="column" gap="5">
            <Flex gap="4">
                <RankingSkeleton />
                <RankingSkeleton />
            </Flex>

            <SurveyQuestionsComparisonTableSkeleton />
        </Flex>
    );
}
